// SpotlightTab — the Profit tab's "Spotlight" sub-tab (Phase B, plan §4.7 Reports).
//
// Answers, in five plain sentences, the only questions the owner asks about
// the spotlight: did every district get its win, is anybody stuck unlucky, how
// many lifetime-lucky players exist, do spotlight winners come back, and what
// did it cost this week (house profit unchanged). Beneath: the district table
// (collapsed, searchable; roster as a column: last winner · when · ₹) and an
// Audit disclosure (influenced rate, LLR histogram, Σ cost per stream, rail
// rides) for the owner's own audit trail.
//
// Backend: GET /api/admin/reports/spotlight (Phase B §B5, lib/spotlightReport.js).
//   OFF ⇒ { off:true, preset:{key,…}, verdicts:[] } + zeros.
//   ON  ⇒ { off:false, preset:{key,stored,custom,updatedAt}, generatedAt,
//           verdicts:[5×{id,text,tone:'green'|'amber'|'red'|'neutral',numbers}],
//           coverage:{text,tone,pct}, districts:[{districtId,name,state,activeUsers,
//           holdout,status:'served'|'overdue'|'measuring'|'below_min',armed:<count>,
//           lastWinner:{userId,username,at,amount,game}|null,…}], audit:{…} }
// The strip has NO top-level "measuring" flag: the backend's measuring signal is
// the first verdict ('districts') carrying tone 'amber' (no district reaches
// golden_min_active yet — code map R-B2), plus coverage.tone 'amber' (< 60%).
// Every field is still read through tolerant normalisers (the obvious aliases
// are accepted) so a shape tweak degrades to "—", never a crash. Verdict
// sentences are rendered VERBATIM from the server — this file has no analytics
// copy of its own.

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '@baaligames/shared-ui/components/Icon';
import { adminApi } from '@baaligames/shared-ui/admin/lib/adminApi';
import { ApiError } from '@baaligames/shared-ui/lib/api';
import { INPUT_CLS } from '../../components/fieldStyles';

// ─── Defensive types ────────────────────────────────────────────────

type Tone = 'ok' | 'amber' | 'bad' | 'neutral';
type VerdictItem = { id?: string; text: string; tone?: Tone };

type DistrictRow = {
  id: string;
  name: string;
  state: string;
  active: number | null;
  lastWinner: string | null;
  lastAt: string | null;
  lastAmt: number | null;
  status: string;          // served | overdue | measuring | armed | '' (free text from server tolerated)
  holdout: boolean;
  overdue: boolean;
  armed: boolean;
};

type Report = {
  off: boolean;
  preset: string | null;
  measuring: boolean;
  coveragePct: number | null;
  verdict: VerdictItem[];
  coverageLine: string | null;
  districts: DistrictRow[];
  audit: Record<string, unknown> | null;
  generatedAt: string | null;
  raw: Record<string, unknown>;
};

const num = (v: unknown): number | null => {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
const str = (v: unknown): string => (v == null ? '' : String(v));
const pick = (o: Record<string, unknown> | null | undefined, ...keys: string[]): unknown => {
  if (!o) return undefined;
  for (const k of keys) if (o[k] !== undefined && o[k] !== null) return o[k];
  return undefined;
};
const inr = (n: number) => `₹${Math.round(n).toLocaleString('en-IN')}`;

function normVerdict(raw: Record<string, unknown>): VerdictItem[] {
  // The server key is the plural `verdicts` (spotlightReport.js getSpotlightReport);
  // the singular/nested spellings are kept as tolerated aliases only.
  let src: unknown = pick(raw, 'verdicts', 'verdict', 'sentences', 'strip');
  if (src && !Array.isArray(src) && typeof src === 'object') {
    src = pick(src as Record<string, unknown>, 'sentences', 'items', 'lines');
  }
  if (!Array.isArray(src)) return [];
  return src
    .map((s): VerdictItem | null => {
      if (typeof s === 'string') return { text: s };
      if (s && typeof s === 'object') {
        const o = s as Record<string, unknown>;
        const text = str(pick(o, 'text', 'sentence', 'label', 'message'));
        const t = str(pick(o, 'tone', 'state', 'status')).toLowerCase();
        const tone: Tone | undefined =
          t === 'ok' || t === 'green' || t === 'good' ? 'ok'
          : t === 'amber' || t === 'warn' || t === 'warning' || t === 'measuring' ? 'amber'
          : t === 'bad' || t === 'red' || t === 'fail' ? 'bad'
          : t ? 'neutral' : undefined;
        const id = str(pick(o, 'id', 'key')) || undefined;
        return text ? { id, text, tone } : null;
      }
      return null;
    })
    .filter((x): x is VerdictItem => !!x);
}

function normDistricts(raw: Record<string, unknown>): DistrictRow[] {
  let src: unknown = pick(raw, 'districts', 'table', 'rows');
  if (src && !Array.isArray(src) && typeof src === 'object') src = pick(src as Record<string, unknown>, 'rows', 'items');
  if (!Array.isArray(src)) return [];
  return src.map((r, i): DistrictRow => {
    const o = (r && typeof r === 'object' ? r : {}) as Record<string, unknown>;
    // The server's `lastWinner` is an OBJECT {userId, username, at, amount, game}
    // (spotlightReport.js) — that object IS the roster column. A string-valued
    // `lastWinner` / `last_winner` is tolerated as a bare username.
    const lw = pick(o, 'lastWinner', 'last_winner');
    const roster = (pick(o, 'roster', 'last', 'lastSpotlight', 'last_spotlight')
      ?? (lw && typeof lw === 'object' ? lw : null)
      ?? {}) as Record<string, unknown>;
    const flatWinner = typeof lw === 'string' ? lw : str(pick(o, 'winner'));
    const idRaw = pick(o, 'district_id', 'districtId', 'id');
    const id = idRaw != null ? String(idRaw) : `row-${i}`;
    const statusRaw = str(pick(o, 'status', 'state_label', 'label')).toLowerCase();
    const holdout = !!(pick(o, 'holdout', 'measuring', 'is_holdout') ?? (statusRaw === 'measuring'));
    const overdue = !!(pick(o, 'overdue', 'is_overdue') ?? (statusRaw === 'overdue'));
    // `armed` arrives as a COUNT of armed roster slots (0 = nobody lined up).
    const armed = !!(pick(o, 'armed', 'is_armed') ?? (str(pick(roster, 'status')).toLowerCase() === 'armed'));
    return {
      id,
      name: str(pick(o, 'name', 'district', 'district_name', 'districtName')) || `District #${id}`,
      state: str(pick(o, 'state', 'state_name', 'stateName')),
      active: num(pick(o, 'active_users', 'active', 'activeUsers', 'players')),
      lastWinner: (str(pick(roster, 'username', 'winner', 'last_winner', 'user')) || flatWinner) || null,
      lastAt: (str(pick(roster, 'fired_at', 'when', 'at', 'firedAt')) || str(pick(o, 'last_fired_at', 'lastFiredAt', 'last_win_at', 'when'))) || null,
      lastAmt: num(pick(roster, 'fired_amount', 'amount', 'firedAmount')) ?? num(pick(o, 'last_amount', 'lastAmount', 'fired_amount')),
      status: statusRaw || (holdout ? 'measuring' : overdue ? 'overdue' : armed ? 'armed' : ''),
      holdout, overdue, armed,
    };
  });
}

/** Exported for the smoke test — turns whatever the report endpoint sends into the shape the tab renders. */
export function normaliseSpotlightReport(raw: unknown): Report {
  const o = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const stateStr = str(pick(o, 'state', 'status')).toLowerCase();
  // `preset` is the server's {key, stored, custom, updatedAt} object — `custom`
  // is ALWAYS present there (null for the named presets), so read `key` first
  // and only spell out the percentage when the key is 'custom'.
  const presetRaw = pick(o, 'preset', 'key');
  let preset: string | null = null;
  if (typeof presetRaw === 'string') preset = presetRaw;
  else if (presetRaw && typeof presetRaw === 'object') {
    const po = presetRaw as Record<string, unknown>;
    const key = typeof po.key === 'string' ? po.key : (typeof po.stored === 'string' ? po.stored : null);
    const cv = num(po.custom);
    if (key && key !== 'custom') preset = key;
    else if (cv != null) preset = `custom ${cv}%`;
    else preset = key;
  }
  const off = o.off === true || stateStr === 'off' || preset === 'off';
  const coverageObj = (o.coverage && typeof o.coverage === 'object' ? o.coverage : null) as Record<string, unknown> | null;
  const coverage = num(pick(o, 'districtsKnownPct', 'coveragePct', 'districtCoveragePct'))
    ?? num(pick(coverageObj, 'pct', 'percent'))
    ?? (typeof o.coverage === 'number' || typeof o.coverage === 'string' ? num(o.coverage) : null);
  const verdict = normVerdict(o);
  // Measuring = the backend's own signal (the 'districts' verdict is amber when
  // no district reaches golden_min_active; coverage.tone amber below 60%) — the
  // explicit flags/state are accepted too if a later backend adds them.
  const districtsVerdict = verdict.find((v) => v.id === 'districts') ?? verdict[0];
  const measuring = o.measuring === true || stateStr === 'measuring'
    || districtsVerdict?.tone === 'amber'
    || str(pick(coverageObj, 'tone')).toLowerCase() === 'amber'
    || (coverage != null && coverage < 60);
  // Coverage sentence: the server's own text when it sends one ({text,tone,pct}).
  const coverageLineRaw = pick(o, 'coverageLine', 'coverage_line', 'districtsKnownLine') ?? pick(coverageObj, 'text');
  const coverageLine = typeof coverageLineRaw === 'string' ? coverageLineRaw
    : (coverage != null ? `District known for ${Math.round(coverage)}% of active players.` : null);
  const auditRaw = pick(o, 'audit', 'auditNumbers', 'audit_numbers');
  return {
    off, preset, measuring, coveragePct: coverage, verdict, coverageLine,
    districts: normDistricts(o),
    audit: auditRaw && typeof auditRaw === 'object' ? (auditRaw as Record<string, unknown>) : null,
    generatedAt: str(pick(o, 'generatedAt', 'generated_at', 'asOf', 'as_of')) || null,
    raw: o,
  };
}

// ─── Component ──────────────────────────────────────────────────────

export default function SpotlightTab() {
  const [report, setReport] = useState<Report | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [showDistricts, setShowDistricts] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  const [q, setQ] = useState('');

  async function load() {
    setLoading(true); setErr('');
    try {
      const d = await adminApi.get<unknown>('/admin/reports/spotlight');
      setReport(normaliseSpotlightReport(d));
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not load the spotlight report.');
    } finally { setLoading(false); }
  }
  useEffect(() => { void load(); }, []);

  const filtered = useMemo(() => {
    if (!report) return [];
    const needle = q.trim().toLowerCase();
    if (!needle) return report.districts;
    return report.districts.filter((d) =>
      d.name.toLowerCase().includes(needle) || d.state.toLowerCase().includes(needle)
      || (d.lastWinner ?? '').toLowerCase().includes(needle));
  }, [report, q]);

  const amber = !!report && !report.off && report.measuring;

  return (
    <div className="max-w-6xl mx-auto px-4 py-4 space-y-4 text-arena-900 dark:text-neon-100">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-arena-900 dark:text-white flex items-center gap-2">
            <Icon name="auto_awesome" size={20} /> Spotlight — who is winning
          </h1>
          <p className="text-[13px] text-arena-500 dark:text-neon-200/70">
            Did every district get its spotlight win, is anyone stuck unlucky, and what did it cost.
            House profit is unchanged by any of this.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {report?.preset && !report.off && (
            <span className="text-[11px] font-bold px-2 py-1 rounded-full
                             bg-violet-50 text-violet-700 ring-1 ring-inset ring-violet-200/60
                             dark:bg-violet-400/10 dark:text-violet-300 dark:ring-violet-400/30 capitalize">
              {report.preset}
            </span>
          )}
          <button
            onClick={() => void load()}
            disabled={loading}
            className="inline-flex items-center gap-1 px-3 py-2 text-[12.5px] font-bold rounded-full
                       bg-white text-arena-700 ring-1 ring-inset ring-arena-200/60 hover:bg-arena-50
                       dark:bg-arena-700/40 dark:text-neon-200/85 dark:ring-arena-600/40 dark:hover:bg-arena-700/60
                       disabled:opacity-50 transition active:scale-95"
          >
            <Icon name="refresh" size={14} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {err && (
        <div className="rounded-2xl px-4 py-3 text-[13px]
                        bg-rose-50 text-rose-800 ring-1 ring-inset ring-rose-200/60
                        dark:bg-rose-500/10 dark:text-rose-200 dark:ring-rose-400/30">
          {err}
        </div>
      )}

      {loading && !report && (
        <div className="rounded-2xl bg-white dark:bg-arena-700/40 ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                        p-8 text-center text-[13px] text-arena-600 dark:text-neon-200/70">
          <Icon name="progress_activity" size={18} className="animate-spin inline-block mr-1.5" /> Loading…
        </div>
      )}

      {/* OFF state — plan §4.6(d): reports show OFF. */}
      {report?.off && (
        <div className="rounded-2xl p-5 bg-white dark:bg-arena-700/40 ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40">
          <div className="flex items-start gap-3">
            <span className="grid place-items-center w-10 h-10 rounded-xl shrink-0
                             bg-arena-100/70 text-arena-600 dark:bg-arena-800/60 dark:text-neon-200/70">
              <Icon name="power_settings_new" size={20} />
            </span>
            <div className="min-w-0">
              <div className="font-bold text-[15px] text-arena-900 dark:text-white">Spotlight is OFF</div>
              <p className="text-[12.5px] text-arena-600 dark:text-neon-200/70 mt-0.5 leading-snug">
                Every game runs on plain chance inside its house target — exactly today's behaviour.
                Nothing is being tracked or spent, so there is nothing to report here.
              </p>
              <Link to="/game-money?tab=earnings"
                    className="inline-flex items-center gap-1 mt-2 text-[12.5px] font-bold text-violet-600 dark:text-violet-300 hover:underline">
                Turn it on under Earnings → Who wins <Icon name="arrow_forward" size={14} />
              </Link>
            </div>
          </div>
        </div>
      )}

      {report && !report.off && (
        <>
          {/* Verdict strip — five sentences, verbatim from the server. AMBER
              while measuring / district coverage < 60% (plan §4.4). */}
          <div className={`rounded-2xl p-4 sm:p-5 ring-1 ring-inset
            ${amber
              ? 'bg-amber-50 ring-amber-300/70 text-amber-900 dark:bg-amber-500/10 dark:ring-amber-400/30 dark:text-amber-100'
              : 'bg-white ring-arena-200/50 text-arena-900 dark:bg-arena-700/40 dark:ring-arena-600/40 dark:text-neon-100'}`}>
            <div className="flex items-center gap-2 mb-3">
              <Icon name={amber ? 'hourglass_top' : 'verified'} size={18}
                    className={amber ? 'text-amber-600 dark:text-amber-300' : 'text-emerald-600 dark:text-emerald-300'} />
              <h2 className="font-bold text-[14px]">
                {amber ? 'Still measuring' : 'This week at a glance'}
              </h2>
              {amber && (
                <span className="text-[11px] font-semibold text-amber-800/80 dark:text-amber-200/80">
                  — not enough districts or players yet for a firm verdict
                </span>
              )}
              {report.generatedAt && (
                <span className="ml-auto text-[11px] text-arena-500 dark:text-neon-200/55">
                  as of {new Date(report.generatedAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                </span>
              )}
            </div>
            {report.verdict.length === 0 ? (
              <p className="text-[12.5px] text-arena-600 dark:text-neon-200/70">
                No verdict available — the report came back without its sentences. Try Refresh.
              </p>
            ) : (
              <ol className="space-y-2">
                {report.verdict.map((v, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-[13px] leading-snug">
                    <span className={`shrink-0 mt-0.5 grid place-items-center w-5 h-5 rounded-full text-[10.5px] font-bold
                      ${v.tone === 'bad'
                        ? 'bg-rose-100 text-rose-700 dark:bg-rose-400/20 dark:text-rose-300'
                        : v.tone === 'amber' || amber
                          ? 'bg-amber-200/70 text-amber-900 dark:bg-amber-400/25 dark:text-amber-100'
                          : v.tone === 'ok'
                            ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-400/20 dark:text-emerald-300'
                            : 'bg-arena-100 text-arena-700 dark:bg-arena-800/60 dark:text-neon-200/80'}`}>
                      {i + 1}
                    </span>
                    <span>{v.text}</span>
                  </li>
                ))}
              </ol>
            )}
            {report.coverageLine && (
              <p className={`mt-3 pt-3 border-t text-[12px] font-semibold
                ${amber ? 'border-amber-300/50 dark:border-amber-400/20' : 'border-arena-200/50 dark:border-arena-600/40 text-arena-600 dark:text-neon-200/70'}`}>
                <Icon name="location_on" size={13} className="inline -mt-0.5 mr-1" />
                {report.coverageLine}
                {report.coveragePct != null && report.coveragePct < 60 && ' Reads amber until 60% — players are asked once where they play from.'}
              </p>
            )}
          </div>

          {/* District table — collapsed by default, searchable. */}
          <div className="rounded-2xl bg-white dark:bg-arena-700/40 ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40 overflow-hidden">
            <button
              onClick={() => setShowDistricts((v) => !v)}
              className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-arena-50/60 dark:hover:bg-arena-700/60 transition"
              aria-expanded={showDistricts}
            >
              <Icon name="map" size={18} className="text-cyan-600 dark:text-cyan-300" />
              <span className="font-bold text-[14px] text-arena-900 dark:text-white">Districts</span>
              <span className="text-[11.5px] text-arena-500 dark:text-neon-200/60">
                {report.districts.length} active · last spotlight winner per district
              </span>
              <Icon name={showDistricts ? 'expand_less' : 'expand_more'} size={20} className="ml-auto text-arena-500 dark:text-neon-200/60" />
            </button>
            {showDistricts && (
              <div className="px-4 pb-4 space-y-3">
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search district, state or winner…"
                  className={INPUT_CLS + ' max-w-sm'}
                />
                {filtered.length === 0 ? (
                  <p className="text-[12.5px] text-arena-600 dark:text-neon-200/70 py-3 text-center">
                    {report.districts.length === 0 ? 'No active districts yet.' : 'No district matches.'}
                  </p>
                ) : (
                  <div className="overflow-x-auto rounded-xl ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40">
                    <table className="w-full text-[12.5px] border-collapse min-w-[640px]">
                      <thead>
                        <tr className="bg-arena-50/80 dark:bg-arena-800/60 text-left text-[11px] uppercase tracking-wider text-arena-600 dark:text-neon-200/65">
                          <th className="p-2.5 font-bold">District</th>
                          <th className="p-2.5 font-bold text-right">Active players</th>
                          <th className="p-2.5 font-bold">Last spotlight win</th>
                          <th className="p-2.5 font-bold">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filtered.map((d) => (
                          <tr key={d.id} className="border-t border-arena-200/40 dark:border-arena-700/40">
                            <td className="p-2.5">
                              <div className="font-semibold text-arena-900 dark:text-white">{d.name}</div>
                              {d.state && <div className="text-[11px] text-arena-500 dark:text-neon-200/55">{d.state}</div>}
                            </td>
                            <td className="p-2.5 text-right tabular-nums">{d.active != null ? d.active.toLocaleString('en-IN') : '—'}</td>
                            <td className="p-2.5">
                              {d.lastWinner || d.lastAt || d.lastAmt != null ? (
                                <div className="flex flex-wrap items-baseline gap-x-1.5">
                                  {d.lastWinner && <span className="font-semibold text-arena-900 dark:text-white">@{d.lastWinner.replace(/^@/, '')}</span>}
                                  {d.lastAt && <span className="text-[11px] text-arena-500 dark:text-neon-200/55">· {relTime(d.lastAt)}</span>}
                                  {d.lastAmt != null && <span className="text-[12px] font-bold text-emerald-700 dark:text-emerald-300 tabular-nums">· {inr(d.lastAmt)}</span>}
                                </div>
                              ) : (
                                <span className="text-arena-400 dark:text-neon-200/40 italic">none yet</span>
                              )}
                            </td>
                            <td className="p-2.5"><StatusChip d={d} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Audit disclosure — the owner's own audit trail (plan §4.7). */}
          <div className="rounded-2xl bg-white dark:bg-arena-700/40 ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40 overflow-hidden">
            <button
              onClick={() => setShowAudit((v) => !v)}
              className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-arena-50/60 dark:hover:bg-arena-700/60 transition"
              aria-expanded={showAudit}
            >
              <Icon name="fact_check" size={18} className="text-violet-600 dark:text-violet-300" />
              <span className="font-bold text-[14px] text-arena-900 dark:text-white">Audit</span>
              <span className="text-[11.5px] text-arena-500 dark:text-neon-200/60">
                influenced rounds · cost per stream · rail rides
              </span>
              <Icon name={showAudit ? 'expand_less' : 'expand_more'} size={20} className="ml-auto text-arena-500 dark:text-neon-200/60" />
            </button>
            {showAudit && (
              <div className="px-4 pb-4">
                {!report.audit || Object.keys(report.audit).length === 0 ? (
                  <p className="text-[12.5px] text-arena-600 dark:text-neon-200/70 py-2">No audit numbers yet.</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {Object.entries(report.audit).map(([k, v]) => (
                      <AuditBlock key={k} label={humanKey(k)} value={v} />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Pieces ─────────────────────────────────────────────────────────

function StatusChip({ d }: { d: DistrictRow }) {
  // Server statuses: measuring (holdout) | below_min | served | overdue; `armed`
  // (a lined-up player) is a separate count. below_min is the launch-time
  // norm (code map R-B2: no district reaches golden_min_active yet) and must
  // read as "nothing scheduled", never as "served".
  const belowMin = !d.holdout && !d.overdue && !d.armed && (d.status === 'below_min' || d.status === 'too few players');
  const cls = d.holdout
    ? 'bg-amber-50 text-amber-800 ring-amber-200/70 dark:bg-amber-400/15 dark:text-amber-300 dark:ring-amber-400/30'
    : d.overdue
      ? 'bg-rose-50 text-rose-700 ring-rose-200/60 dark:bg-rose-400/15 dark:text-rose-300 dark:ring-rose-400/30'
      : d.armed
        ? 'bg-violet-50 text-violet-700 ring-violet-200/60 dark:bg-violet-400/15 dark:text-violet-300 dark:ring-violet-400/30'
        : belowMin
          ? 'bg-arena-100/70 text-arena-600 ring-arena-200/60 dark:bg-arena-800/50 dark:text-neon-200/70 dark:ring-arena-600/40'
          : 'bg-emerald-50 text-emerald-700 ring-emerald-200/60 dark:bg-emerald-400/15 dark:text-emerald-300 dark:ring-emerald-400/30';
  const label = d.holdout ? 'measuring' : d.overdue ? 'overdue' : d.armed ? 'lined up' : belowMin ? 'too few players' : (d.status || 'served');
  const title = d.holdout
    ? 'Holdout district — no spotlight here on purpose, so we can measure the difference it makes.'
    : d.overdue ? 'This district has waited longer than the preset promises.'
      : d.armed ? 'A player here is lined up for a spotlight win this window.'
        : belowMin ? 'Fewer than the minimum active players for a spotlight — nothing is scheduled here yet.'
          : 'Served within the promised gap.';
  return (
    <span title={title} className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10.5px] font-bold ring-1 ring-inset capitalize ${cls}`}>
      {label}
    </span>
  );
}

/** Generic renderer for whatever the audit object carries: numbers, tables, key/value maps. */
function AuditBlock({ label, value }: { label: string; value: unknown }) {
  return (
    <div className="rounded-xl p-3 bg-arena-50/50 dark:bg-arena-800/40 ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40">
      <div className="text-[10.5px] uppercase tracking-wider font-bold text-arena-600 dark:text-neon-200/65 mb-1.5">{label}</div>
      <AuditValue value={value} />
    </div>
  );
}

function AuditValue({ value }: { value: unknown }) {
  if (value == null) return <span className="text-[12px] text-arena-400 dark:text-neon-200/40">—</span>;
  if (typeof value === 'number' || typeof value === 'string' || typeof value === 'boolean') {
    return <div className="text-[18px] font-display font-extrabold text-arena-900 dark:text-white tabular-nums">{fmtScalar(value)}</div>;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="text-[12px] text-arena-400 dark:text-neon-200/40">none</span>;
    if (value.every((x) => x == null || typeof x !== 'object')) {
      return <div className="text-[12.5px] text-arena-800 dark:text-neon-100">{value.map(fmtScalar).join(', ')}</div>;
    }
    const cols = Array.from(new Set(value.flatMap((r) => (r && typeof r === 'object' ? Object.keys(r as object) : []))));
    return (
      <div className="overflow-x-auto">
        <table className="w-full text-[12px] border-collapse">
          <thead>
            <tr className="text-left text-[10.5px] uppercase tracking-wider text-arena-500 dark:text-neon-200/55">
              {cols.map((c) => <th key={c} className="py-1 pr-3 font-bold whitespace-nowrap">{humanKey(c)}</th>)}
            </tr>
          </thead>
          <tbody>
            {value.map((r, i) => (
              <tr key={i} className="border-t border-arena-200/40 dark:border-arena-700/40">
                {cols.map((c) => (
                  <td key={c} className="py-1 pr-3 tabular-nums text-arena-800 dark:text-neon-100 whitespace-nowrap">
                    {fmtScalar((r as Record<string, unknown>)?.[c])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>);
    if (entries.length === 0) return <span className="text-[12px] text-arena-400 dark:text-neon-200/40">none</span>;
    return (
      <dl className="space-y-1">
        {entries.map(([k, v]) => (
          <div key={k} className="flex items-start justify-between gap-3 text-[12.5px]">
            <dt className="text-arena-600 dark:text-neon-200/70">{humanKey(k)}</dt>
            <dd className="text-right font-semibold text-arena-900 dark:text-white tabular-nums min-w-0">
              {v != null && typeof v === 'object' ? <AuditValue value={v} /> : fmtScalar(v)}
            </dd>
          </div>
        ))}
      </dl>
    );
  }
  return <span className="text-[12px]">{String(value)}</span>;
}

function fmtScalar(v: unknown): string {
  if (v == null) return '—';
  if (typeof v === 'boolean') return v ? 'yes' : 'no';
  if (typeof v === 'number') return Number.isInteger(v) ? v.toLocaleString('en-IN') : v.toLocaleString('en-IN', { maximumFractionDigits: 3 });
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

function humanKey(k: string): string {
  const s = k.replace(/[_-]+/g, ' ').replace(/([a-z0-9])([A-Z])/g, '$1 $2').trim().toLowerCase();
  return s
    .replace(/\bllr\b/g, 'LLR')
    .replace(/\bpct\b/g, '%')
    .replace(/\bq(\d)\b/g, 'quarter $1')
    .replace(/^./, (c) => c.toUpperCase());
}

function relTime(ts: string): string {
  const t = new Date(ts).getTime();
  if (!Number.isFinite(t)) return ts;
  const ms = Date.now() - t;
  if (ms < 60_000) return 'just now';
  const mins = Math.floor(ms / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(ts).toLocaleDateString('en-IN', { dateStyle: 'medium' });
}
